import { PAUSE_CHOICES, SPEED_PRESETS, type LiveSettings, type SpeedPreset } from "../../hooks/liveSettings";

interface Props {
  settings: LiveSettings;
  onChange: (patch: Partial<LiveSettings>) => void;
  /** False when the server has live lines switched off (LIVE_PARTIALS=false): the toggle can't have any effect. */
  serverAllowsLivePreview: boolean;
  micLive: boolean;
  /** How long the server took to translate the last chunks (ms), or null before the first one. */
  delay?: { lastMs: number; avgMs: number; maxMs: number } | null;
}

const seconds = (ms: number) => `${(ms / 1000).toFixed(1)} s`;

export default function LiveSettingsCard({ settings, onChange, serverAllowsLivePreview, micLive, delay }: Props) {
  const preset = SPEED_PRESETS[settings.speed];
  // Translation time only (speech recognition and the chunk itself add to it): under 1 s is comfortable.
  const health = delay ? (delay.avgMs <= 1000 ? "ok" : delay.avgMs <= 2000 ? "warn" : "bad") : null;
  return (
    <div className="card">
      <strong>Live caption settings</strong>
      <p style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
        How quickly captions reach listeners. Changes apply immediately, and are remembered on this computer.
      </p>

      <div className="field" style={{ marginTop: 12 }}>
        <label>Translation speed</label>
        <div className="lang-grid" role="radiogroup" aria-label="Translation speed">
          {(Object.keys(SPEED_PRESETS) as SpeedPreset[]).map((key) => (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={settings.speed === key}
              className={`lang-choice${settings.speed === key ? " selected" : ""}`}
              onClick={() => onChange({ speed: key })}
            >
              {SPEED_PRESETS[key].label}
            </button>
          ))}
        </div>
        <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 6 }}>{preset.hint}</div>
        <div style={{ fontSize: 12.5, marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {delay && health ? (
            <>
              <span className={`status-pill ${health}`}>
                <span className="status-dot" />
                Translation time {seconds(delay.avgMs)}
              </span>
              <span style={{ color: "var(--text-muted)" }}>
                average of the last chunks · latest {seconds(delay.lastMs)} · slowest {seconds(delay.maxMs)}
                {health === "bad" ? " — slow: check the internet connection, or pick Fastest" : ""}
              </span>
            </>
          ) : (
            <span style={{ color: "var(--text-muted)" }}>Translation time is shown here once the first chunk has been translated.</span>
          )}
        </div>
      </div>

      <div className="field" style={{ marginTop: 14 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: serverAllowsLivePreview ? "pointer" : "not-allowed" }}>
          <input
            type="checkbox"
            checked={serverAllowsLivePreview && settings.livePreview}
            disabled={!serverAllowsLivePreview}
            onChange={(e) => onChange({ livePreview: e.target.checked })}
            style={{ width: 18, height: 18 }}
          />
          <span>Live line for listeners</span>
        </label>
        <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 4 }}>
          {serverAllowsLivePreview
            ? "Shows a dashed, continuously updating translation while the speaker is mid-phrase. Turn it off to translate only finished chunks and use fewer Translator characters."
            : "Switched off on the server (LIVE_PARTIALS=false in backend/.env)."}
        </div>
      </div>

      <div className="field" style={{ marginTop: 14 }}>
        <label htmlFor="pause-ms">Pause that ends a phrase</label>
        <select id="pause-ms" value={settings.pauseMs} onChange={(e) => onChange({ pauseMs: Number(e.target.value) })}>
          {PAUSE_CHOICES.map((ms) => (
            <option key={ms} value={ms}>
              {(ms / 1000).toFixed(1)} seconds{ms === 500 ? " (recommended)" : ""}
            </option>
          ))}
        </select>
        <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 4 }}>
          {micLive
            ? "Applies the next time you press Start mic."
            : "Shorter = captions start sooner but sentences may split; longer = fuller sentences."}
        </div>
      </div>
    </div>
  );
}
